import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { catchError, EMPTY, filter, first } from 'rxjs';

import { SendReferralRequest, UserInfo } from '@blaze-shared/api-contracts-domain';
import { ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import { AccountsApiService } from '@blaze-shared/auth-data-access';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { SelectComponent } from '@blaze-shared/shared-feature';
import { ButtonComponent, DialogComponent, ShowFormErrorPipe } from '@blaze-shared/shared-ui';
import { SelectOption } from '@blaze-shared/shared-util';

@Component({
  selector: 'blaze-referral-dialog',
  imports: [
    ButtonComponent,
    DialogComponent,
    FormsModule,
    MatIconModule,
    ReactiveFormsModule,
    SelectComponent,
    ShowFormErrorPipe,
  ],
  templateUrl: './referral-dialog.component.html',
  styleUrl: './referral-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralDialogComponent {
  private readonly accountsApiService = inject(AccountsApiService);
  private readonly dialogRef = inject(MatDialogRef<ReferralDialogComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly notificationService = inject(NotificationService);
  private readonly projectContextFacade = inject(ProjectContextFacade);

  protected readonly form = this.fb.group({
    additionalInfo: this.fb.nonNullable.control<string>(''),
    company: this.fb.nonNullable.control<string>(''),
    email: this.fb.nonNullable.control<string>('', [Validators.required, Validators.email]),
    name: this.fb.nonNullable.control<string>(''),
    source: this.fb.nonNullable.control<string>(''),
    sourceTyped: this.fb.nonNullable.control<string>(''),
  });

  protected readonly howDoYouKnowOptions: SelectOption<string, string>[] = [
    'Colleague or former colleague',
    'Industry peer',
    'Friend',
    'Vendor or partner',
    'Other',
  ].map((option) => ({ label: option, value: option }));

  protected readonly user: Signal<UserInfo | undefined>;
  protected readonly name: Signal<string>;

  constructor() {
    this.user = toSignal(this.projectContextFacade.userInfo$);
    this.name = computed(() =>
      `${this.user()?.firstName ?? ''} ${this.user()?.lastName ?? ''}`.trim(),
    );
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }

  protected onSendReferral(): void {
    this.projectContextFacade.accountId$.pipe(filter(Boolean), first()).subscribe((accountId) => {
      const value = this.form.getRawValue();
      const request: SendReferralRequest = {
        additionalInfo: value.additionalInfo,
        company: value.company,
        email: value.email,
        name: value.name,
        source: value.source === 'Other' ? value.sourceTyped : value.source,
      };

      this.accountsApiService
        .postSendReferral(accountId, request)
        .pipe(
          catchError((err) => {
            this.notificationService.showError(err);
            return EMPTY;
          }),
        )
        .subscribe(() => {
          this.notificationService.info('Thank you for sending the referral.');
          this.closeDialog();
        });
    });
  }
}
